import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Copy, User, Store, ShieldCheck, ArrowLeftCircle, Sparkles, LogIn, Loader2, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  superadmin: { label: 'SuperAdmin', color: 'bg-[#4A6B53]/10 text-[#4A6B53]', Icon: ShieldCheck, destination: '/superadmin' },
  admin: { label: 'Admin Negozio', color: 'bg-[#C25934]/10 text-[#C25934]', Icon: Store, destination: '/admin' },
  customer: { label: 'Cliente', color: 'bg-[#8B5CF6]/10 text-[#8B5CF6]', Icon: User, destination: '/' },
};

export default function TestProfilesPage() {
  const { user, loading: authLoading, isSuperAdmin, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [impersonationInfo, setImpersonationInfo] = useState({ impersonating: false });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/superadmin/test-profiles`, { withCredentials: true });
      setProfiles(data.profiles || []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Solo SuperAdmin può accedere a questa pagina');
      } else {
        toast.error('Errore caricamento profili');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImpersonationStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/superadmin/impersonation-status`, { withCredentials: true });
      setImpersonationInfo(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchImpersonationStatus();
  }, [fetchImpersonationStatus]);

  useEffect(() => {
    if (authLoading) return;
    if (user && isSuperAdmin) {
      fetchProfiles();
    } else if (user && !isSuperAdmin && !impersonationInfo.impersonating) {
      navigate('/');
    }
  }, [user, authLoading, isSuperAdmin, fetchProfiles, navigate, impersonationInfo.impersonating]);

  const seedProfiles = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/superadmin/seed-test-profiles`, {}, { withCredentials: true });
      toast.success('Profili test creati/aggiornati');
      await fetchProfiles();
    } catch (e) {
      toast.error('Errore creazione profili');
    } finally {
      setSeeding(false);
    }
  };

  const impersonate = async (profile) => {
    if (profile.id === user?.id) {
      navigate(ROLE_META[profile.role]?.destination || '/');
      return;
    }
    setImpersonatingId(profile.id);
    try {
      const { data } = await axios.post(`${API}/superadmin/impersonate/${profile.id}`, {}, { withCredentials: true });
      await refreshUser();
      toast.success(`Accesso come ${data.name || data.email}`);
      const dest = ROLE_META[data.role]?.destination || '/';
      setTimeout(() => navigate(dest), 100);
    } catch (e) {
      toast.error('Errore impersonation');
    } finally {
      setImpersonatingId(null);
    }
  };

  const stopImpersonating = async () => {
    try {
      await axios.post(`${API}/superadmin/stop-impersonating`, {}, { withCredentials: true });
      await refreshUser();
      await fetchImpersonationStatus();
      toast.success('Tornato SuperAdmin');
      navigate('/superadmin/test-profiles');
    } catch (e) {
      toast.error('Errore ripristino sessione');
    }
  };

  const copyCred = (profile) => {
    const text = `Email: ${profile.email}\nPassword: ${profile.password_hint}`;
    navigator.clipboard.writeText(text).then(() => toast.success('Credenziali copiate'));
  };

  // Banner visible when impersonating (even if user isn't SuperAdmin anymore)
  if (impersonationInfo.impersonating) {
    return (
      <div data-testid="impersonation-banner-page" className="px-4 pt-4 pb-safe">
        <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="text-amber-600" size={22} />
            <h2 className="font-bold text-amber-900 text-lg" style={{ fontFamily: 'Outfit' }}>Sessione di test attiva</h2>
          </div>
          <p className="text-sm text-amber-800">
            Stai navigando come <b>{impersonationInfo.current_name}</b> ({impersonationInfo.current_email}) — ruolo <b>{impersonationInfo.current_role}</b>.
          </p>
          <Button
            data-testid="stop-impersonating-btn"
            onClick={stopImpersonating}
            className="w-full h-11 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold"
          >
            <ArrowLeftCircle size={18} className="mr-2" /> Torna SuperAdmin
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 pt-8 text-center">
        <Loader2 size={32} className="mx-auto animate-spin text-[#C25934]" />
      </div>
    );
  }

  return (
    <div data-testid="test-profiles-page" className="px-4 pt-4 pb-safe space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            🧪 Profili Test
          </h1>
          <p className="text-xs text-[#877E78] mt-0.5">Simula tutti i flussi con un click</p>
        </div>
        <Button
          data-testid="seed-profiles-btn"
          onClick={seedProfiles}
          disabled={seeding}
          variant="outline"
          className="h-10 rounded-full border-[#E8E3D9] text-[#5C554F] text-sm"
        >
          {seeding ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCcw size={14} className="mr-1.5" />}
          Rigenera
        </Button>
      </div>

      <div className="bg-[#C25934]/5 border border-[#C25934]/20 rounded-xl p-3 text-xs text-[#5C554F]">
        💡 Clicca <b>"Accedi come"</b> per entrare nella sessione di un ruolo e provare tutte le funzionalità. Un banner in alto ti permetterà di tornare SuperAdmin in qualsiasi momento.
      </div>

      {profiles.length === 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E3D9] p-6 text-center">
          <User size={36} className="mx-auto text-[#877E78] mb-2" />
          <p className="text-sm text-[#5C554F] mb-3">Nessun profilo test trovato</p>
          <Button data-testid="initial-seed-btn" onClick={seedProfiles} disabled={seeding} className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full h-10">
            {seeding ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
            Crea profili demo
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {profiles.map(p => {
          const meta = ROLE_META[p.role] || ROLE_META.customer;
          const { Icon } = meta;
          const isMe = p.id === user?.id;
          return (
            <div key={p.id} data-testid={`profile-card-${p.role}`} className="bg-white rounded-2xl border border-[#E8E3D9] p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta.color}`}>
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                      {p.name || p.email}
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.color}`}>
                      {meta.label}
                    </span>
                    {isMe && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">TU</span>}
                  </div>
                  <p className="text-xs text-[#877E78] mt-0.5 truncate">{p.email}</p>
                  {p.shop && (
                    <p className="text-xs text-[#5C554F] mt-1 flex items-center gap-1">
                      <Store size={11} className="inline" /> {p.shop.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-[#F7F3EC] rounded-xl p-2.5 text-xs text-[#5C554F] flex items-center justify-between">
                <div className="min-w-0">
                  <span className="font-mono truncate block">pwd: {p.password_hint}</span>
                </div>
                <button
                  data-testid={`copy-cred-${p.role}`}
                  onClick={() => copyCred(p)}
                  className="ml-2 p-1.5 rounded-lg hover:bg-[#E6E2D6] text-[#877E78]"
                  title="Copia credenziali"
                >
                  <Copy size={14} />
                </button>
              </div>

              <Button
                data-testid={`login-as-${p.role}`}
                onClick={() => impersonate(p)}
                disabled={impersonatingId === p.id}
                className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-medium"
              >
                {impersonatingId === p.id ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Accesso...</>
                ) : (
                  <><LogIn size={16} className="mr-2" /> {isMe ? `Vai alla ${meta.label}` : `Accedi come ${meta.label}`}</>
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
